import React from 'react';
import { PlayButton } from 'components/UI/PlayButton';
import { Spinner } from 'components/UI/Spinner';
import { LogService } from '@landr/core';
import { PrismicHTML } from 'components/Prismic';
import { SegmentService } from 'analytics';
import { Grid, GridCol, GridRow } from '../../UI/Grid';
import { log } from '../../../log';
import { EventId } from '../../../enum/eventId';
import { withAppContext } from '../../../context/AppContext';
import { ABPlayersSources, Item, MasteringDemosProps, MasteringDemosState, Versions } from './types';
import { ABPlayers } from './ABPlayers';
import { StyleImage } from './StyleImage';
import { BtnVersion } from './BtnVersion';
import { Style } from './Style';
import { backgroundResize, componentDidMountAnimation, moveToggle, setProgressBgToDefault } from './Animations';
import './MasteringDemos.scss';

const createABPlayersSources = (items: Item[]): ABPlayersSources[] =>
    items.reduce(
        (accumulator, style) => [
            ...accumulator,
            {
                [Versions.original]: style.mastering_demos_original.url,
                [Versions.mastered]: style.mastering_demos_mastered.url,
            },
        ],
        [],
    );

class MasteringDemosComponent extends React.PureComponent<MasteringDemosProps, MasteringDemosState> {
    state = {
        selectedStyle: 0,
        isPlaying: false,
        selectedVersion: Versions.original,
    };

    SelectedBackgroundBtn: React.RefObject<any>;
    BtnOriginal: React.RefObject<any>;
    BtnMastered: React.RefObject<any>;
    Spinner: React.RefObject<any>;
    ProgressBg: React.RefObject<any>;
    originalBtnWidth: number;
    masterBtnWidth: number;
    log: LogService;

    componentDidMount() {
        this.log = log.getInstance('MasteringDemos');

        componentDidMountAnimation();

        window.addEventListener('resize', () => {
            backgroundResize(this.BtnOriginal, this.BtnMastered, this.SelectedBackgroundBtn);
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', () => {
            backgroundResize(this.BtnOriginal, this.BtnMastered, this.SelectedBackgroundBtn);
        });
    }

    componentDidCatch(error: Error) {
        this.log.error('Mastering Demos slice failed', EventId.AbPlayerFailed, error);
    }

    setSelectedStyle(i: number) {
        this.setState({
            selectedStyle: i,
            isPlaying: false,
            selectedVersion: Versions.original,
        });
        this.animateToggle(Versions.original);
        SegmentService.trackCarouselSlideChanged(this.props.sliceContext);
    }

    setSelectedVersion(version: Versions) {
        this.setState({
            selectedVersion: version,
        });

        this.originalBtnWidth = this.BtnOriginal.current.offsetWidth;
        this.masterBtnWidth = this.BtnMastered.current.offsetWidth;
        this.SelectedBackgroundBtn.current.style.width = `${this.originalBtnWidth}px`;
        if (!this.state.isPlaying) {
            this.play();
        }

        this.animateToggle(version);

        SegmentService.trackAudioPlayerToggled(
            {
                'L - Asset Version': version,
            },
            this.props.sliceContext,
        );
    }

    animateToggle(version: any) {
        if (version === Versions.original) {
            this.SelectedBackgroundBtn.current.style.width = `${this.originalBtnWidth}px`;
            moveToggle(this.SelectedBackgroundBtn.current, 0);
        } else {
            this.SelectedBackgroundBtn.current.style.width = `${this.masterBtnWidth}px`;
            moveToggle(this.SelectedBackgroundBtn.current, this.originalBtnWidth);
        }
    }

    switchPlayerState() {
        this.state.isPlaying === false ? this.play() : this.stop();
    }

    play() {
        this.setState({
            isPlaying: true,
        });
    }

    stop() {
        this.setState({
            isPlaying: false,
        });
    }

    updateLoadingSpinner = (active: boolean) => {
        if (this.Spinner.current === null) {
            return;
        }

        if (active) {
            this.Spinner.current.style.display = 'block';
            setProgressBgToDefault(this.ProgressBg.current);
        } else {
            this.Spinner.current.style.display = 'none';
        }
    };

    render() {
        const {
            slice: {
                primary: {
                    mastering_demos_mastered_button_copy: masteredButtonCopy,
                    mastering_demos_original_button_copy: originalButtonCopy,
                    mastering_demos_title: demosTitle,
                },
                fields: styles,
            },
        } = this.props;

        this.SelectedBackgroundBtn = React.createRef();
        this.BtnOriginal = React.createRef();
        this.BtnMastered = React.createRef();
        this.Spinner = React.createRef();
        this.ProgressBg = React.createRef();
        return (
            <div className="MasteringDemos-fullscreen">
                <Grid className="MasteringDemos-container">
                    <GridRow horizontalAlign={'h-start'} verticalAlign={'v-center'} className="FeaturedArtists-content">
                        <GridCol colSize={`12`} offsetSize={`0`} className="Wrapper-info">
                            <PrismicHTML content={demosTitle} className="MasteringDemos-title" />
                            {styles.map((style, i) => (
                                <div key={i}>
                                    <Style
                                        key={i}
                                        style={style}
                                        setSelectedStyle={() => this.setSelectedStyle(i)}
                                        isSelectedStyle={i === this.state.selectedStyle}
                                    />
                                </div>
                            ))}
                            <GridCol
                                colSize={`6`}
                                offsetSize={`0`}
                                mobileColSize={'12'}
                                className="Wrapper-info-bottom"
                            >
                                <div className="Wrapper-abcontrol">
                                    <canvas id="Progress-bar" width="100px" height="100px" />
                                    <div className="Progress-bg" ref={this.ProgressBg} />
                                    <PlayButton
                                        isPlaying={this.state.isPlaying}
                                        audioAction={() => this.switchPlayerState()}
                                        className="MasteringDemos-playButton"
                                    />
                                    <Spinner className="Spinner" spinnerRef={this.Spinner} />
                                    <ABPlayers
                                        sources={createABPlayersSources(styles)}
                                        version={this.state.selectedVersion}
                                        current={this.state.selectedStyle}
                                        isPlaying={this.state.isPlaying}
                                        currentStyle={styles}
                                        sliceContext={this.props.sliceContext}
                                        showSpinner={() => this.updateLoadingSpinner(true)}
                                        hideSpinner={() => this.updateLoadingSpinner(false)}
                                    />
                                </div>
                            </GridCol>
                            <GridCol colSize={`6`} offsetSize={`0`} mobileColSize={'12'} className="Block-version">
                                <BtnVersion
                                    changeVersionToOriginal={() => this.setSelectedVersion(Versions.original)}
                                    changeVersionToMastered={() => this.setSelectedVersion(Versions.mastered)}
                                    selectedVersion={this.state.selectedVersion}
                                    masteredButtonCopy={masteredButtonCopy}
                                    originalButtonCopy={originalButtonCopy}
                                    SelectedBackgroundBtn={this.SelectedBackgroundBtn}
                                    BtnOriginal={this.BtnOriginal}
                                    BtnMastered={this.BtnMastered}
                                />
                            </GridCol>
                        </GridCol>
                    </GridRow>
                </Grid>
                {styles.map((style, i) => (
                    <StyleImage key={i} style={style} isSelectedStyle={i === this.state.selectedStyle} />
                ))}
            </div>
        );
    }
}

const MasteringDemos = withAppContext(MasteringDemosComponent);

export { MasteringDemos, MasteringDemosComponent };
